import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as supplierService from "../../services/supplierService";
import { DataTable, ProcBadge, amount, csvStamp, downloadCsv } from "../Proc/procUi";
import { AppDropdown, EmptyState, LoadState, PageHeader, SecondaryButton, cardStyle, formatDate, inputStyle, pageStyle, useLoad } from "../../components/ui";

// The supplier's RFQs, from every buyer that invited them.
const FILTERS = [
  { label: "All RFQs", value: null },
  { label: "Open for quotation", value: "OPEN,INFO" },
  { label: "Quotation sent", value: "QUOTED,EVALUATION" },
  { label: "Shortlisted", value: "QUALIFIED" },
  { label: "Awarded", value: "AWARDED" },
  { label: "Not accepted or declined", value: "REJECTED,DECLINED,NOT_AWARDED" },
  { label: "Closed", value: "CLOSED,CANCELLED" },
];

function Tile({ label, value, onClick, tone = "#111827" }) {
  return (
    <button type="button" onClick={onClick} style={{ ...cardStyle, padding: "12px 16px", textAlign: "left", border: "1px solid #E5E7EB", background: "#fff" }}>
      <div style={{ fontSize: 12, color: "#6B7280" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: tone }}>{value}</div>
    </button>
  );
}

export default function SupplierRfqs() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [buyer, setBuyer] = useState(null);
  const [typed, setTyped] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(t);
  }, [typed]);

  const summary = useLoad(() => supplierService.getRfqSummary(), []);
  const buyers = useLoad(() => supplierService.getBuyers(), []);
  const { data, loading, error, reload } = useLoad(() => supplierService.listRfqs({ status, buyer_id: buyer, q }), [status, buyer, q]);
  const rows = data || [];
  const s = summary.data || {};
  const exportCsv = () =>
    downloadCsv(`rfqs-${csvStamp()}.csv`, ["RFQ", "Title", "Buyer", "Quote by", "Status", "Quotation", "Currency", "Quotation total"], rows.map((r) => [r.rfq_number, r.title, r.buyer_name, r.responsible_date || "", r.state_label, r.quotation_number || "", r.currency, r.quotation_total ?? ""]));
  const buyerOptions = [{ label: "All buyers", value: null }, ...(buyers.data || []).map((b) => ({ label: b.buyer_name || "Buyer", value: b.buyer_id }))];

  return (
    <div style={pageStyle}>
      <PageHeader title="Requests for quotation" subtitle="RFQs your buyers sent you" actions={<SecondaryButton onClick={exportCsv} disabled={!rows.length}>Export CSV</SecondaryButton>} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }} className="mb-3">
        <Tile label="Open for quotation" value={s.open ?? 0} tone={s.open ? "#065F46" : "#111827"} onClick={() => setStatus("OPEN,INFO")} />
        <Tile label="Quotation sent" value={s.quoted ?? 0} onClick={() => setStatus("QUOTED,EVALUATION")} />
        <Tile label="Shortlisted" value={s.shortlisted ?? 0} onClick={() => setStatus("QUALIFIED")} />
        <Tile label="Awarded" value={s.awarded ?? 0} tone={s.awarded ? "#065F46" : "#111827"} onClick={() => setStatus("AWARDED")} />
        <Tile label="Not accepted" value={s.not_accepted ?? 0} onClick={() => setStatus("REJECTED,DECLINED,NOT_AWARDED")} />
      </div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        <input className="form-control" style={{ ...inputStyle, maxWidth: 280 }} placeholder="Search RFQs" aria-label="Search RFQs" value={typed} onChange={(e) => setTyped(e.target.value)} />
        <div style={{ minWidth: 220 }}>
          <AppDropdown id="sup-rfq-status" value={status} options={FILTERS} onChange={(e) => setStatus(e.value ?? null)} aria-label="Status" />
        </div>
        {(buyers.data || []).length > 1 ? (
          <div style={{ minWidth: 200 }}>
            <AppDropdown id="sup-rfq-buyer" value={buyer} options={buyerOptions} onChange={(e) => setBuyer(e.value ?? null)} aria-label="Buyer" />
          </div>
        ) : null}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          onRowClick={(r) => navigate(`/supplier/rfqs/${r.id}`)}
          empty={
            <EmptyState
              title={q || status || buyer ? "No RFQs match" : "No RFQs yet"}
              text={q || status || buyer ? "Try another search or filter." : "When a buyer sends you a request for quotation it appears here, and you get a notification."}
              action={status || buyer ? null : <Link to="/supplier/buyers">See your buyers</Link>}
            />
          }
          columns={[
            { label: "RFQ", render: (r) => <><div style={{ fontWeight: 600, color: "#111827" }}>{r.title}</div><div style={{ fontSize: 12, color: "#6B7280" }}>{r.rfq_number}</div></> },
            { label: "Buyer", render: (r) => r.buyer_name },
            { label: "Quote by", render: (r) => formatDate(r.responsible_date), style: { whiteSpace: "nowrap" } },
            { label: "Status", render: (r) => <ProcBadge status={r.state} label={r.state_label} /> },
            { label: "Your quotation", render: (r) => (r.quotation_id ? `${r.quotation_number} · ${r.currency} ${amount(r.quotation_total)}` : "—") },
          ]}
        />
      </LoadState>
    </div>
  );
}
